import { useRef } from 'react'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material'
import DownloadIcon from '../../../assets/icons/DownloadIcon'
import {
  ColumnConfigurationIcon,
  FileExcludeIcon,
  FullScreenIcon,
  SearchIconBtn,
} from '../../../assets/icons/index.js'
import { statusTabs } from '../../../constants/prepare.jsx'
import StyledSwitch from '../../common/StyledSwitch'
import DownloadReportPopper from './DownloadReportPopper.jsx'

// Small wrapper to reduce duplication
const ActionIconButton = ({
  title,
  onClick,
  icon,
  color = 'var(--forbrand-primary)',
  disabled,
  anchorRef,
}) => (
  <Tooltip title={title}>
    <span ref={anchorRef}>
      <IconButton
        size="small"
        sx={{ color }}
        onClick={onClick}
        disabled={disabled}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
)

export default function ActionIcons({
  selectedTabIndex,
  includeSwitchOn,
  isFullScreen,
  editMode,
  selectedCount,
  handleColumnConfigClick,
  handleFullscreen,
  handleFullScreenExit,
  handleSaveEdit,
  handleEdit,
  handleSearch,
  handleIncludeSwitchChange,
  handleExcludeItems,
  handleSuggestTDSRate,
  handleCloseEditMode,
  isSuggestedRateLoading,
  isReprocessLoading,
  isCooldown,
  handleReprocess,
  downloadPopperOpen,
  handleDownloadIconClick,
  handleDownloadPopperClose,
  handleReportDownload,
}) {
  const selectedTabScreen = statusTabs[selectedTabIndex].screenName

  const downloadAnchorRef = useRef(null)

  const actions = [
    {
      key: 'includeSwitch',
      title: 'Include excluded, unmapped and adjustment transaction',
      icon: (
        <StyledSwitch
          size="small"
          checked={includeSwitchOn}
          onChange={handleIncludeSwitchChange}
        />
      ),
      applicableScreens: ['PROCESSED'],
    },
    !isFullScreen
      ? {
        key: 'fullscreen',
        title: 'Full Screen',
        icon: <FullScreenIcon />,
        applicableScreens: [
          'UNPROCESSED',
          'PROCESSED',
          'EXCEPTION',
          'EXCLUDED',
          'NEGATIVE',
          'ADJUSTMENT',
        ],
        color: 'var(--forbrand-primary)',
        onClick: handleFullscreen,
      }
      : {
        key: 'exitFullscreen',
        title: 'Exit Full Screen',
        icon: <FullScreenIcon />,
        applicableScreens: [
          'UNPROCESSED',
          'PROCESSED',
          'EXCEPTION',
          'EXCLUDED',
          'NEGATIVE',
          'ADJUSTMENT',
        ],
        onClick: handleFullScreenExit,
      },
    {
      key: 'columnConfig',
      title: 'Column Configuration',
      icon: <ColumnConfigurationIcon />,
      applicableScreens: [
        'UNPROCESSED',
        'PROCESSED',
        'EXCEPTION',
        'EXCLUDED',
        'NEGATIVE',
        'ADJUSTMENT',
      ],
      color: 'var(--forbrand-primary)',
      onClick: handleColumnConfigClick,
    },
    {
      key: 'excludeItems',
      title: `${selectedTabScreen == "PROCESSED" ? "Exclude" : "Include"} all/selected record button`,
      icon: <FileExcludeIcon />,
      applicableScreens: ['PROCESSED', 'EXCLUDED'],
      onClick: handleExcludeItems,
      disabled: !selectedCount,
    },
    {
      key: 'download',
      title: 'Download Excel',
      icon: <DownloadIcon />,
      applicableScreens: [
        'UNPROCESSED',
        'PROCESSED',
        'EXCEPTION',
        'EXCLUDED',
        'ADJUSTMENT',
        'NEGATIVE',
      ],
      onClick: handleDownloadIconClick,
      anchorRef: downloadAnchorRef,
    },
    // editMode
    //   ? {
    //       key: 'save',
    //       title: 'Save',
    //       icon: <CheckIcon />,
    //       applicableScreens: [
    //         'UNPROCESSED',
    //         'PROCESSED',
    //         'EXCEPTION',
    //         'EXCLUDED',
    //         'NEGATIVE',
    //         'ADJUSTMENT',
    //       ],
    //       color: 'var(--icon-green)',
    //       onClick: handleSaveEdit,
    //     }
    //   : {
    //       key: 'edit',
    //       title: 'Edit',
    //       icon: <EditIcon />,
    //       applicableScreens: [
    //         'UNPROCESSED',
    //         'PROCESSED',
    //         'EXCEPTION',
    //         'EXCLUDED',
    //         'NEGATIVE',
    //         'ADJUSTMENT',
    //       ],
    //       color: 'var(--icon-orange)',
    //       onClick: handleEdit,
    //     },
    // editMode && {
    //   key: 'cancel',
    //   title: 'Cancel',
    //   icon: <Close />,
    //   applicableScreens: [
    //     'UNPROCESSED',
    //     'PROCESSED',
    //     'EXCEPTION',
    //     'EXCLUDED',
    //     'NEGATIVE',
    //     'ADJUSTMENT',
    //   ],
    //   color: 'var(--icon-orange)',
    //   onClick: handleCloseEditMode,
    // },
    {
      key: 'search',
      title: 'Search',
      icon: <SearchIconBtn />,
      applicableScreens: [
        'UNPROCESSED',
        'PROCESSED',
        'EXCEPTION',
        'EXCLUDED',
        'NEGATIVE',
        'ADJUSTMENT',
      ],
      color: 'var(--icon-orange)',
      onClick: handleSearch,
    },
  ]

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', cursor: 'pointer' }}>
      {actions
        .filter(action =>
          action?.applicableScreens?.includes(selectedTabScreen)
        )
        .map(action => {
          const props = {
            title: action.title,
            color: action.color,
            icon: action.icon,
            onClick: action.onClick,
            disabled: action.disabled,
            anchorRef: action.anchorRef,
          }
          return <ActionIconButton key={action.key} {...props} />
        })}

      <DownloadReportPopper
        open={downloadPopperOpen}
        anchorEl={downloadAnchorRef.current}
        onClose={handleDownloadPopperClose}
        onDownload={handleReportDownload}
      />
      {selectedTabScreen === 'PROCESSED' && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: 16,
              margin: '9px 4px 0 2px',
              color: 'var(--forbrand-color)',
            }}
          />
          <Button
            variant="contained"
            sx={{
              px: '16px',
              py: '8px',
              height: '32px',
              textTransform: 'none',
              fontWeight: 500,
              marginTop: '1px',
              backgroundColor: 'var(--forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
            }}
            disabled={isSuggestedRateLoading}
            onClick={handleSuggestTDSRate}
          >
            Suggest TDS Rate
          </Button>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: 16,
              margin: '9px 4px 0 2px',
              color: 'var(--forbrand-color)',
            }}
          />
          <Button
            variant="contained"
            sx={{
              px: '16px',
              py: '8px',
              height: '32px',
              textTransform: 'none',
              fontWeight: 500,
              marginTop: '1px',
              backgroundColor: 'var(--forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
            }}
            disabled={isReprocessLoading || isCooldown}
            onClick={handleReprocess}
          >
            Reprocess
          </Button>
        </>
      )}
      {(selectedTabScreen === 'EXCEPTION' ||
        selectedTabScreen === 'UNPROCESSED') && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: 16,
              margin: '9px 4px 0 2px',
              color: 'var(--forbrand-color)',
            }}
          />
          <Button
            variant="contained"
            sx={{
              px: '16px',
              py: '8px',
              height: '32px',
              textTransform: 'none',
              fontWeight: 500,
              marginTop: '1px',
              backgroundColor: 'var(--forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
            }}
            disabled={isReprocessLoading || isCooldown}
            onClick={handleReprocess}
          >
            Reprocess
          </Button>
        </>
      )}
    </Box>
  )
}
